import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { LiveChannelsDataService, LiveChannelsColumnsService } from '../../services';
import { DialogLiveChannelFormComponent } from './live-channel-form/live-channel-form.component';
import { DialogLiveChannelDetailsComponent } from './live-channel-details/live-channel-details.component';
import { LiveChannel, LiveChannelsResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-live-channels',
  templateUrl: './live-channels.component.html',
  styleUrls: ['./live-channels.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class LiveChannelsComponent implements OnInit, OnDestroy {

  private liveChannelAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private liveChannelEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private liveChannelDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public liveChannelToDelete: LiveChannel = {};
  public liveChannels: LiveChannel[] = [];
  public liveChannel: LiveChannel = {};
  public selectedLiveChannels: LiveChannel[] = [];
  public submitted: boolean = false;
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '100'};
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private liveChannelsDataService: LiveChannelsDataService,
    private liveChannelsColumnsService: LiveChannelsColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('LiveChannelsComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Live Channels - Management');
    this.primengConfig.ripple = true;
    this.getLiveChannels();
  }

  ngOnDestroy(): void {
    console.log('LiveChannelsComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getLiveChannels(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.liveChannelsDataService.getLiveChannels(this.parameters).subscribe((response: LiveChannelsResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.liveChannels = response.results;
        } else {
          this.liveChannels = this.liveChannels.concat(response.results);
        }
        if (response.inlineCount > this.liveChannels.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getLiveChannels();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('LIVECHANNELSNOTLOADED')});
    });
  }

  importLiveChannels(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.liveChannelsDataService.addLiveChannels(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('LIVECHANNELSCREATED')});
                if (proceed === j) {
                  this.getLiveChannels();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('LIVECHANNELSNOTCREATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('LIVECHANNELSNOTCREATED') + ' ' + msg});
            });
          } else {
            this.liveChannelsDataService.updateLiveChannels(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('LIVECHANNELSUPDATED')});
                if (proceed === j) {
                  this.getLiveChannels();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('LIVECHANNELSNOTUPDATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('LIVECHANNELSNOTCREATED') + ' ' + msg});
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.liveChannelsColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('liveChannels.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedLiveChannels() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Live Channels?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.liveChannelsDataService.deleteLiveChannels(this.selectedLiveChannels).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({severity:'success', summary: 'Successful', detail: 'Live Channels Deleted', life: 3000});
            this.getLiveChannels();
          } else {
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Live Channels not deleted'});
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({severity: 'error', summary: 'Error', detail: 'Live Channels not deleted'});
        });
        this.selectedLiveChannels = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.liveChannels, this.columns, 'LiveChannels');
  }

  exportExcel() {
    ToolsService.exportExcel(this.liveChannels, this.columns, 'LiveChannels');
  }

  exportCsv() {
    ToolsService.exportCsv(this.liveChannels, this.columns, 'LiveChannels');
  }

  refresh() {
    this.liveChannelsDataService.clearCache();
    this.getLiveChannels();
  }

  liveChannelAdd(): void {
    if (!this.navigation.getParamValue('liveChannels.addInDialog', true)) {
      this.router.navigate(['/live-channels/add']);
      return;
    }
    this.liveChannelAddDialog = this.dialogService.open(DialogLiveChannelFormComponent, {
      header: '', // this.translate.instant('NEWLIVECHANNEL'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        liveChannel: {id: null},
        prices: this.liveChannelsColumnsService.prices,
        sharings: this.liveChannelsColumnsService.sharings,
        users: this.liveChannelsColumnsService.users,
      },
    });

    this.liveChannelAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'info', summary: 'Success', detail: 'LiveChannel added'});
        this.getLiveChannels();
      }
    });
  }

  liveChannelEdit(liveChannel: LiveChannel): void {
    if (!this.navigation.getParamValue('liveChannels.editInDialog', true)) {
      this.router.navigate(['/live-channels/edit/' + liveChannel.id]);
      return;
    }
    this.liveChannelEditDialog = this.dialogService.open(DialogLiveChannelFormComponent, {
      header: '', // this.translate.instant('EDITLIVECHANNEL') + ' ' + liveChannel.name,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        liveChannel: liveChannel,
        prices: this.liveChannelsColumnsService.prices,
        sharings: this.liveChannelsColumnsService.sharings,
        users: this.liveChannelsColumnsService.users,
      },
    });

    this.liveChannelEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'LiveChannel updated'});
        this.getLiveChannels();
      }
    });
  }

  liveChannelDetails(liveChannel: LiveChannel): void {
    if (!this.navigation.getParamValue('liveChannels.detailsInDialog', true)) {
      this.router.navigate(['/live-channels/details/' + liveChannel.id]);
      return;
    }
    this.liveChannelDetailsDialog = this.dialogService.open(DialogLiveChannelDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        liveChannel: liveChannel,
      }
    });
  }

  liveChannelDelete(liveChannel: LiveChannel): void {
    this.liveChannelToDelete = liveChannel;
    this.messageService.clear();
    this.messageService.add({
      key: 'liveChannelDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + liveChannel.name + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.liveChannelsDataService.deleteLiveChannel(this.liveChannelToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'Live Channel deleted'});
      this.getLiveChannels();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Live Channel not deleted'});
    });
  }

  onDeleteReject(): void {
    this.liveChannelToDelete = {};
    this.messageService.clear('liveChannelDelete');
  }

}
